import React, { Fragment, useState } from 'react';

import './InboxList.scss';
import { useMobile } from '@helpers/hooks';
import { getString, capitalizeFirstLetter } from '@helpers/styles';
import sendEvent from '@helpers/tracking';
import { formatDateNl } from '@helpers/date';

import ChevronRight from '../../shared/assets/icons/redesign/chevron-right.svg';
import ChevronDown from '../../shared/assets/icons/redesign/chevron-down.svg';
import ChevronUp from '../../shared/assets/icons/redesign/chevron-up.svg';
import Inbox from '../../shared/assets/icons/redesign/inbox.svg';

export default function InboxList({ list, user, NextLink }) {
  const [isMobile] = useMobile();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="inboxList">
      {isMobile ? (
        <Fragment>
          <div
            className={getString([
              { condition: true, value: 'inboxList-header' },
              { condition: isOpen, value: 'inboxList-header--opened' }
            ])}
            onClick={() => list.length && setIsOpen(!isOpen)}
          >
            <div>
              <Inbox className="inboxList-inbox-icon" />
              <h4 className="inboxList-title">Taken</h4>
              <div className="inboxList-length">{list.length}</div>
            </div>

            <div>
              <ChevronUp className="inboxList-chevron-up-icon" />
              <ChevronDown className="inboxList-chevron-down-icon" />
            </div>
          </div>

          <List list={list} isOpen={isOpen} NextLink={NextLink} isMobile />
        </Fragment>
      ) : (
        <Fragment>
          <h4 className="inboxList-title">
            Taken
            {!list.length && (
              <span className="inboxList-length-desktop inboxList-length">
                0
              </span>
            )}
          </h4>

          <List list={list} NextLink={NextLink} isMobile={false} />
        </Fragment>
      )}
    </div>
  );
}

function List({ list, isOpen, isMobile, NextLink }) {
  return (
    <ul
      className="inboxList-list"
      {...(isOpen && { style: { maxHeight: `${6 * list.length}rem` } })}
    >
      {list.map((item) => (
        <ListItem
          item={item}
          key={`${getItemKey(item)}`}
          NextLink={NextLink}
          isMobile={isMobile}
        />
      ))}
    </ul>
  );
}

function getItemKey(item) {
  return item?.xuid || item?.id || JSON.stringify(item);
}

function isXFormAnswer(item) {
  return !!item?.formName && !item?.recipient && !item?.form;
}

function getItemTitle(item) {
  return item?.form?.name || item?.formName || item?.name || '—';
}

function getItemHref(item) {
  if (isXFormAnswer(item)) return `/xform-answers/${item.xuid}`;
  if (item?.form) return `/form-answers/${item.xuid}`;
  return `/form-answers/create/${item.xuid}/${item.recipient}`;
}

function getRecipientLabel(item) {
  if (item?.recipient) return capitalizeFirstLetter(item.recipient);
  if (item?.createdBy) return capitalizeFirstLetter(item.createdBy);
  return null;
}

export function ListItem({ item, NextLink, isMobile, onClick }) {
  const href = getItemHref(item);
  const title = getItemTitle(item);
  const recipientLabel = getRecipientLabel(item);
  const createdAtLabel = formatDateNl(item?.createdAt);

  return (
    <li
      className="inboxList-list-item"
      onClick={() => {
        if (onClick) onClick();

        if (isMobile)
          sendEvent(
            ...(isMobile
              ? ['dashboard-partner-mobile', 'dashboard-partner-mobile-inbox']
              : [
                  'dashboard-partner-desktop',
                  'dashboard-partner-desktop-inbox'
                ])
          );
      }}
    >
      <NextLink prefetch={false} href={href} passHref legacyBehavior>
        <a target="_blank" rel="noopener noreferrer" className="inboxList-link">
          <div className="inboxList-list-item-text">
            <div className="inboxList-list-item-marker">&#8226;</div>

            <div>
              <p className="inboxList-list-item-title">{title}</p>

              {(recipientLabel || createdAtLabel) && (
                <div className="inboxList-meta">
                  {recipientLabel && (
                    <span className="inboxList-list-item-subtitle">
                      {recipientLabel}
                    </span>
                  )}

                  {recipientLabel && createdAtLabel && (
                    <span className="inboxList-meta-separator">·</span>
                  )}

                  {createdAtLabel && (
                    <span className="inboxList-list-item-date">
                      {createdAtLabel}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <ChevronRight className="inboxList-list-item-icon" />
        </a>
      </NextLink>
    </li>
  );
}
